<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref } from "vue";
import { useUiStore } from "@/app/stores/uiStore";


import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import IconBlueBox from "@/components/icons/IconBlueBox.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import AdminRequestMaterialDialog from "@/components/shared/Material/AdminRequestMaterialDialog.vue";
import AdminReviewMaterialRequestDialog from "@/components/shared/Material/AdminReviewMaterialRequestDialog.vue";
import CompleteMaterialRequestDialog from "@/components/shared/Material/CompleteMaterialRequestDialog.vue";

import EmployeeService from "@/api/services/employee.service";
import MaterialTypeService from "@/api/services/materialType.service";
import MaterialRequestService from "@/api/services/materialRequest.service";
import { filterByNormalizedText } from "@/utils/textSearch";

const uiStore = useUiStore();

const employees = ref([]);
const employeeSearchQuery = ref("");
const materialTypes = ref([]);
const requests = ref([]);
const totalResults = ref(0);
const page = ref(1);
const itemsPerPage = ref(30);

const searchEmployee = ref();
const searchMaterialType = ref();
const searchStatus = ref();
const appliedFilters = ref({});

const showCreateDialog = ref(false);
const showReviewDialog = ref(false);
const showFulfillModal = ref(false);
const showApproveModal = ref(false);
const showRejectModal = ref(false);
const selectedRequest = ref(null);
const approveSystemsComment = ref("");
const rejectSystemsComment = ref("");
const rejectCommentError = ref(false);

const statusOptions = {
  pending: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
  fulfilled: "Completada",
  fullfilled: "Completada",
};
const requestStatusOptions = ref([
  { name: "Pendiente", value: "pending" },
  { name: "Aprobada", value: "approved" },
  { name: "Rechazada", value: "rejected" },
  { name: "Completada", value: "fulfilled" },
]);

onMounted(async () => {
  uiStore.startLoading();
  try {
    await Promise.all([
      getEmployees(false),
      getMaterialTypes(false),
      getMaterialRequests(false),
    ]);
  } finally {
    uiStore.stopLoading();
  }
});

const filteredRequests = computed(() => requests.value);
const filteredEmployees = computed(() => {
  return filterByNormalizedText(employees.value, employeeSearchQuery.value, employee => employee.fullName);
});

const search = async () => {
  appliedFilters.value = {
    ...(searchEmployee.value ? { "requester.id": searchEmployee.value } : {}),
    ...(searchMaterialType.value ? { "materialType.id": searchMaterialType.value } : {}),
    ...(searchStatus.value ? { status: searchStatus.value } : {}),
  };
  page.value = 1;
  await getMaterialRequests();
};

const openCreateDialog = () => {
  showCreateDialog.value = true;
};

const closeCreateDialog = () => {
  showCreateDialog.value = false;
};

const openReviewDialog = async (request) => {
  await getMaterialRequest(request.id);
};

const closeReviewDialog = () => {
  showReviewDialog.value = false;
  selectedRequest.value = null;
};

const openFulfillModal = (request) => {
  selectedRequest.value = request;
  showFulfillModal.value = true;
};

const closeFulfillModal = () => {
  showFulfillModal.value = false;
  selectedRequest.value = null;
};

const openApproveModal = (request) => {
  selectedRequest.value = request;
  approveSystemsComment.value = request?.systemsComment || "";
  showApproveModal.value = true;
};

const closeApproveModal = () => {
  showApproveModal.value = false;
  selectedRequest.value = null;
  approveSystemsComment.value = "";
};

const openRejectModal = (request) => {
  selectedRequest.value = request;
  rejectSystemsComment.value = request?.systemsComment || "";
  rejectCommentError.value = false;
  showRejectModal.value = true;
};

const closeRejectModal = () => {
  showRejectModal.value = false;
  selectedRequest.value = null;
  rejectSystemsComment.value = "";
  rejectCommentError.value = false;
};

const saveRequest = async (payload) => {
  const params = {
    description: payload.description || "",
    systemsComment: payload.systemsComment || "",
    status: "pending",
    materialType: payload.materialType["@id"],
  };

  uiStore.startLoading();
  try {
    const res = await MaterialRequestService.createMaterialRequest(params);
    if (res && (res.status === 200 || res.status === 201)) {
      closeCreateDialog();
      page.value = 1;
      await getMaterialRequests(false);

      notification.addNotification({
        type: "success",
        message: "Solicitud creada correctamente.",
        duration: 5000,
      });
    }
  } finally {
    uiStore.stopLoading();
  }
};

const approveRequestSelected = async () => {
  if (!selectedRequest.value) {
    return;
  }

  uiStore.startLoading();
  try {
    const res = await MaterialRequestService.approveMaterialRequest(selectedRequest.value.id, {
      systemsComment: approveSystemsComment.value.trim(),
    });
    if (res && (res.status === 200 || res.status === 201)) {
      closeApproveModal();
      await getMaterialRequests(false);

      notification.addNotification({
        type: "success",
        message: "Solicitud aprobada correctamente.",
        duration: 5000,
      });
    }
  } finally {
    uiStore.stopLoading();
  }
};

const fulfillRequestSelected = async (payload) => {
  if (!selectedRequest.value || selectedRequest.value.status !== "approved" ||
      !payload.material?.["@id"] || !payload.assignedAt) {
    return;
  }

  uiStore.startLoading();
  try {
    const res = await MaterialRequestService.fulfillMaterialRequest(selectedRequest.value.id, {
      material: payload.material["@id"],
      assignedAt: payload.assignedAt,
      notes: payload.notes || "",
    });
    if (res && (res.status === 200 || res.status === 201)) {
      closeFulfillModal();
      closeReviewDialog();
      await getMaterialRequests(false);

      notification.addNotification({
        type: "success",
        message: "Solicitud completada correctamente.",
        duration: 5000,
      });
    }
  } finally {
    uiStore.stopLoading();
  }
};

const handleUpdateRequest = async (payload) => {
  if (!selectedRequest.value) {
    return;
  }

  const params = {
    description: selectedRequest.value.description || "",
    systemsComment: payload.systemsComment || "",
    materialType: selectedRequest.value.materialType?.["@id"],
  };

  const currentStatus = selectedRequest.value.status || "pending";
  const targetStatus = payload.status || currentStatus;
  const isApproval = currentStatus === "pending" && targetStatus === "approved";
  const isRejection = currentStatus === "pending" && targetStatus === "rejected";
  const isCompletion = currentStatus === "approved" && targetStatus === "fulfilled";
  if (targetStatus !== currentStatus && !isApproval && !isRejection && !isCompletion) return;
  if (isRejection && !params.systemsComment.trim()) {
    notification.addNotification({ type: "error", message: "Debes introducir un comentario de sistemas.", duration: 5000 });
    return;
  }
  if (isCompletion && (!payload.material?.["@id"] || !payload.assignedAt)) return;

  uiStore.startLoading();
  try {
    const id = selectedRequest.value.id;
    const saved = await MaterialRequestService.updateMaterialRequest(id, params);
    if (!saved || ![200, 201].includes(saved.status)) return;

    let res = saved;
    let message = "Solicitud actualizada correctamente.";
    if (isApproval) {
      res = await MaterialRequestService.approveMaterialRequest(id, {
        systemsComment: params.systemsComment.trim(),
      });
      message = "Solicitud aprobada correctamente.";
    } else if (isRejection) {
      res = await MaterialRequestService.rejectMaterialRequest(id, {
        systemsComment: params.systemsComment.trim(),
      });
      message = "Solicitud rechazada correctamente.";
    } else if (isCompletion) {
      res = await MaterialRequestService.fulfillMaterialRequest(id, {
        material: payload.material["@id"],
        assignedAt: payload.assignedAt,
        notes: payload.notes || "",
      });
      message = "Solicitud completada correctamente.";
    }
    if (res && [200, 201].includes(res.status)) {
      closeReviewDialog();
      await getMaterialRequests(false);
      notification.addNotification({ type: "success", message, duration: 5000 });
    }
  } finally {
    uiStore.stopLoading();
  }
};

const rejectRequestSelected = async () => {
  if (!selectedRequest.value) {
    return;
  }

  rejectCommentError.value = !rejectSystemsComment.value.trim();
  if (rejectCommentError.value) {
    return;
  }

  uiStore.startLoading();
  try {
    const res = await MaterialRequestService.rejectMaterialRequest(selectedRequest.value.id, {
      systemsComment: rejectSystemsComment.value.trim(),
    });
    if (res && (res.status === 200 || res.status === 201)) {
      closeRejectModal();
      await getMaterialRequests(false);

      notification.addNotification({
        type: "success",
        message: "Solicitud rechazada correctamente.",
        duration: 5000,
      });
    }
  } finally {
    uiStore.stopLoading();
  }
};

const getEmployees = async (showLoading = true) => {
  const params = {
    active: true,
    "order[name]": "asc",
    page: 1,
    itemsPerPage: 999999,
  };

  if (showLoading) uiStore.startLoading();
  try {
    const res = await EmployeeService.getEmployees(params);
    if (res && res.status === 200) {
      employees.value = res.data["hydra:member"];
    }
  } finally {
    if (showLoading) uiStore.stopLoading();
  }
};
const filterEmployeesByName = (query) => {
  employeeSearchQuery.value = query;
};

const getMaterialTypes = async (showLoading = true) => {
  const params = {
    page: 1,
    itemsPerPage: 99999,
  };

  if (showLoading) uiStore.startLoading();
  try {
    const res = await MaterialTypeService.getMaterialTypes(params);
    if (res && res.status === 200) {
      materialTypes.value = res.data["hydra:member"].map((item) => ({
        ...item,
        id: item.id || extractId(item["@id"]),
      }));
    }
  } finally {
    if (showLoading) uiStore.stopLoading();
  }
};

const getMaterialRequests = async (showLoading = true) => {
  const params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    "order[requestedAt]": "DESC",
    ...appliedFilters.value,
  };

  if (showLoading) uiStore.startLoading();
  try {
    const res = await MaterialRequestService.getMaterialRequests(params);
    if (res && res.status === 200) {
      requests.value = normalizeRequests(res.data["hydra:member"]);
      totalResults.value = res.data["hydra:totalItems"] || 0;
    }
  } finally {
    if (showLoading) uiStore.stopLoading();
  }
};

const getMaterialRequest = async (id) => {
  uiStore.startLoading();
  try {
    const res = await MaterialRequestService.getMaterialRequest(id);
    if (res && res.status === 200) {
      selectedRequest.value = normalizeRequest(res.data);
      showReviewDialog.value = true;
    }
  } finally {
    uiStore.stopLoading();
  }
};

const extractId = (iri) => {
  if (!iri) {
    return null;
  }

  const parts = iri.split("/");
  return Number(parts[parts.length - 1]);
};

const normalizeRequest = (request) => {
  if (!request) {
    return request;
  }

  return {
    ...request,
    employee: request.employee || request.requester || null,
    materialType: request.materialType || request.type || null,
    systemsComment: request.systemsComment || "",
  };
};

const normalizeRequests = (list = []) => {
  return list.map((item) => normalizeRequest(item));
};

const getStatusLabel = (status) => {
  return statusOptions[status] || "Pendiente";
};

const getStatusClass = (status) => {
  switch (status) {
    case "assigned":
    case "approved":
      return "status-approved";
    case "rejected":
      return "status-rejected";
    case "fulfilled":
      return "status-fulfilled";
    default:
      return "status-pending";
  }
};

const canRejectRequest = (request) => {
  if (!request || !request.status) {
    return true;
  }

  return request.status === "pending";
};

const canApproveRequest = (request) => {
  if (!request || !request.status) {
    return true;
  }

  return request.status === "pending";
};

const canFulfillRequest = (request) => {
  if (!request || !request.status) {
    return false;
  }

  return request.status === "approved";
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const pad = (number) => String(number).padStart(2, "0");

  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
</script>

<template>
  <div>
    <HeaderTitle title="Sistemas" />
    <div class="d-flex flex-row justify-content-start">
      <IconBlueBox class="iconBlueBox" />
      <div class="title-text pl-4">
        Solicitar Material
      </div>
    </div>

    <el-row>
      <el-card class="card-search w-100 wback" body-style="padding:0">
        <div class="row">
          <div class="col-xl-4 col-md-6 col-lg-4">
            <div class="form-item w-100 label-common">
              <label for="request-employee" class="w-100">Empleado</label>
              <el-select id="request-employee" v-model="searchEmployee" filterable clearable placeholder="TODOS"
                :filter-method="filterEmployeesByName"
                @change="search">
                <el-option v-for="item in filteredEmployees" :key="item.id" :label="item.fullName" :value="item.id" />
              </el-select>
            </div>
          </div>
          <div class="col-xl-4 col-md-6 col-lg-4">
            <div class="form-item w-100 label-common pl-5">
              <label for="request-type" class="w-100">Tipo de material</label>
              <el-select id="request-type" v-model="searchMaterialType" filterable clearable placeholder="TODOS"
                @change="search">
                <el-option v-for="item in materialTypes" :key="item.id || item['@id']" :label="item.name"
                  :value="item.id" />
              </el-select>
            </div>
          </div>
          <div class="col-xl-4 col-md-6 col-lg-4">
            <div class="form-item w-100 label-common pl-5">
              <label for="request-status" class="w-100">Estado</label>
              <el-select id="request-status" v-model="searchStatus" filterable clearable placeholder="TODOS"
                @change="search">
                <el-option v-for="item in requestStatusOptions" :key="item.value" :label="item.name"
                  :value="item.value" />
              </el-select>
            </div>
          </div>
          <div class="col-xl-12 col-md-12 col-lg-12 request-search-actions">
            <div class="btn-group-responsive">
              <button class="btn btn_red btn__new" @click="openCreateDialog()">
                Nueva solicitud
              </button>
              <button class="btn btn_yellow btn-search btn__search" @click="search()">
                Buscar
              </button>
            </div>
          </div>
        </div>
      </el-card>
    </el-row>

    <div class="table table-material table-not-mobile">
      <div v-if="filteredRequests.length > 0">
        <table>
          <thead>
            <tr class="justify-content-start gap-2">
              <th>FECHA DE SOLICITUD</th>
              <th>EMPLEADO</th>
              <th>TIPO</th>
              <th>DESCRIPCIÓN</th>
              <th>COMENTARIO SISTEMAS</th>
              <th>ESTADO</th>
              <th>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="request in filteredRequests" :key="request.id">
              <td class="pointer" @click="openReviewDialog(request)">
                {{ formatDateTime(request.requestedAt) }}
              </td>
              <td class="pointer" @click="openReviewDialog(request)">
                {{ request.employee?.fullName || "" }}
              </td>
              <td class="pointer" @click="openReviewDialog(request)">
                {{ request.materialType?.name || "" }}
              </td>
              <td class="pointer description-cell" @click="openReviewDialog(request)">
                {{ request.description || "" }}
              </td>
              <td class="pointer description-cell" @click="openReviewDialog(request)">
                {{ request.systemsComment || "" }}
              </td>
              <td class="pointer status-cell" @click="openReviewDialog(request)">
                <span :class="getStatusClass(request.status)">
                  {{ getStatusLabel(request.status) }}
                </span>
              </td>
              <td>
                <div class="buttons d-inline-flex justify-content-end">
                  <div class="pointer">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Revisar solicitud"
                      @click="openReviewDialog(request)">
                      <font-awesome-icon icon="circle" style="color: #f7d20e" />
                      <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                  </div>
                  <div class="pointer" v-if="canApproveRequest(request)">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Aprobar solicitud"
                      @click="openApproveModal(request)">
                      <font-awesome-icon icon="circle" style="color:#27ae60" />
                      <font-awesome-icon icon="check" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                  </div>
                  <div class="pointer" v-if="canFulfillRequest(request)">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Completar solicitud"
                      @click="openFulfillModal(request)">
                      <font-awesome-icon icon="circle" style="color:#107dde" />
                      <font-awesome-icon icon="check" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                  </div>
                  <div class="pointer" v-if="canRejectRequest(request)">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Rechazar solicitud"
                      @click="openRejectModal(request)">
                      <font-awesome-icon icon="circle" style="color:red" />
                      <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <SharedElementPagination class="pagination" v-model:current-page="page"
          :total="totalResults" v-model:page-size="itemsPerPage" style="padding: 0px 0px 20px 0px;"
          @change="page = $event; getMaterialRequests()" show-always />
      </div>
      <div v-else>
        <h5>No se han encontrado solicitudes según los filtros especificados...</h5>
      </div>
    </div>

    <div class="table-mobile">
      <div v-if="filteredRequests.length > 0">
        <div v-for="request in filteredRequests" :key="request.id">
          <el-row class="mt-4">
            <el-card class="card-request-item">
              <div class="request-card-action request-card-actions">
                <font-awesome-layers v-if="canApproveRequest(request)" class="fa-2x pointer" title="Aprobar solicitud"
                  @click.stop="openApproveModal(request)">
                  <font-awesome-icon icon="circle" style="color:#27ae60" />
                  <font-awesome-icon icon="check" class="fa-inverse" transform="shrink-6" />
                </font-awesome-layers>
                <font-awesome-layers v-if="canFulfillRequest(request)" class="fa-2x pointer" title="Completar solicitud"
                  @click.stop="openFulfillModal(request)">
                  <font-awesome-icon icon="circle" style="color:#107dde" />
                  <font-awesome-icon icon="check" class="fa-inverse" transform="shrink-6" />
                </font-awesome-layers>
                <font-awesome-layers v-if="canRejectRequest(request)" class="fa-2x pointer" title="Rechazar solicitud"
                  @click.stop="openRejectModal(request)">
                  <font-awesome-icon icon="circle" style="color:red" />
                  <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                </font-awesome-layers>
              </div>
              <div class="row d-flex pointer" @click="openReviewDialog(request)">
                <div class="el-col-xs-24 request-main-row">
                  <span class="request-item-span">Fecha de solicitud: </span>
                  {{ formatDateTime(request.requestedAt) }}
                </div>
                <div class="el-col-xs-24" v-if="request.employee">
                  <span class="request-item-span">Empleado: </span>{{ request.employee.fullName }}
                </div>
                <div class="el-col-xs-24" v-if="request.materialType">
                  <span class="request-item-span">Tipo: </span>{{ request.materialType.name }}
                </div>
                <div class="el-col-xs-24" v-if="request.description">
                  <span class="request-item-span">Descripción: </span>{{ request.description }}
                </div>
                <div class="el-col-xs-24" v-if="request.systemsComment">
                  <span class="request-item-span">Comentario sistemas: </span>{{ request.systemsComment }}
                </div>
                <div class="el-col-xs-24 request-status-row">
                  <span :class="getStatusClass(request.status)">
                    {{ getStatusLabel(request.status) }}
                  </span>
                </div>
              </div>
            </el-card>
          </el-row>
        </div>
        <SharedElementPagination class="pagination" v-model:current-page="page"
          :total="totalResults" v-model:page-size="itemsPerPage" style="padding: 0px 0px 20px 0px;"
          @change="page = $event; getMaterialRequests()" show-always />
      </div>
      <div v-else>
        <h5>No se han encontrado solicitudes según los filtros especificados...</h5>
      </div>
    </div>

    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="showCreateDialog" width="601" :show-close="false" :lock-scroll="true"
      @close="closeCreateDialog">
      <AdminRequestMaterialDialog v-if="showCreateDialog" :employees="employees" :material-types="materialTypes"
        @close-dialog="closeCreateDialog" @save-request="saveRequest" />
    </el-dialog>

    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="showReviewDialog" width="601" :show-close="false" :lock-scroll="true"
      @close="closeReviewDialog">
      <AdminReviewMaterialRequestDialog v-if="showReviewDialog" :request="selectedRequest"
        @close-dialog="closeReviewDialog" @save-request="handleUpdateRequest"
        @fulfill-request="handleUpdateRequest({ ...$event, status: 'fulfilled' })" />
    </el-dialog>

    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="showFulfillModal" width="601" :show-close="false" :lock-scroll="true"
      @close="closeFulfillModal">
      <CompleteMaterialRequestDialog v-if="showFulfillModal" :request="selectedRequest"
        @close-dialog="closeFulfillModal" @fulfill-request="fulfillRequestSelected" />
    </el-dialog>

    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="showApproveModal" :title="'¿Desea aprobar esta solicitud?'" :show-close="false" width="600"
      @close="closeApproveModal">
      <div class="mb-4">
        <label class="d-block">Comentario de sistemas</label>
        <el-input v-model="approveSystemsComment" type="textarea" :rows="4"
          placeholder="Escribe un comentario de sistemas" class="custom-textarea" />
      </div>
      <div class="row item justify-content-center align-content-center material-modal-confirm-actions">
        <div class="col-sm-4">
          <button class="btn btn_yellow" @click="closeApproveModal()">
            Cerrar
          </button>
        </div>
        <div class="col-sm-4">
          <button class="btn btn_green" @click="approveRequestSelected()">
            Aprobar
          </button>
        </div>
      </div>
    </el-dialog>

    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="showRejectModal" :title="'¿Desea rechazar esta solicitud?'" :show-close="false" width="600"
      @close="closeRejectModal">
      <div class="mb-4">
        <label class="d-block">Comentario de sistemas</label>
        <el-input v-model="rejectSystemsComment" type="textarea" :rows="4"
          placeholder="Escribe un comentario de sistemas" class="custom-textarea" />
        <small v-if="rejectCommentError" class="text-danger">
          Debes introducir un comentario de sistemas
        </small>
      </div>
      <div class="row item justify-content-center align-content-center material-modal-confirm-actions">
        <div class="col-sm-4">
          <button class="btn btn_yellow" @click="closeRejectModal()">
            Cerrar
          </button>
        </div>
        <div class="col-sm-4">
          <button class="btn btn_red" @click="rejectRequestSelected()">
            Rechazar
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.btn__search,
.btn__new {
  width: 200px;
  height: 3em;
}

.iconBlueBox {
  width: 30px;
  fill: $bg-blue;
}

.btn-group-responsive {
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;

    button {
      width: 100% !important;
    }
  }
}

.request-search-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.description-cell {
  max-width: 280px;
  white-space: normal;
}

.custom-textarea:deep(.el-textarea__inner) {
  font-size: 14px !important;
  border-radius: 20px;
}

.status-cell {
  text-align: center;
  vertical-align: middle;
}

.status-pending,
.status-approved,
.status-rejected,
.status-fulfilled {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #fff;
  text-align: center;
  text-transform: uppercase;
}

.status-pending {
  background-color: #f1c40f;
}

.status-approved {
  background-color: #27ae60;
}

.status-rejected {
  background-color: #E53935;
}

.status-fulfilled {
  background-color: #107dde;
}

.card-request-item {
  border-radius: 16px;
  font-size: 15px;
  position: relative;
}

.card-request-item:deep(.el-card__body) {
  padding: 18px 16px;
}

.request-card-action {
  position: absolute;
  right: 12px;
  top: 10px;
  z-index: 1;
}

.request-card-actions {
  display: flex;
  gap: 8px;
}

.request-main-row {
  padding-right: 88px;
}

.request-item-span {
  color: $bg-blue;
  font-weight: 800;
}

.request-status-row {
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
  padding-right: 42px;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

@media (max-width: 767px) {
  .table-not-mobile {
    display: none;
  }

  .card-search {
    margin-top: 12px;
  }

  .card-search:deep(.el-card__body) {
    padding: 16px 12px !important;
  }

  .card-search .row {
    margin-left: 0;
    margin-right: 0;
  }

  .card-search [class*="col-"] {
    padding-left: 0;
    padding-right: 0;
  }

  .card-search .form-item {
    margin-top: 0.75rem !important;
    padding-left: 0 !important;
  }

  .card-search .el-select {
    width: 100% !important;
  }

  .btn-group-responsive {
    padding-top: 6px;
  }

  .item {
    .col-sm-4 {
      margin-bottom: 0.75rem;
    }
  }
}
</style>
